import { onAuthStateChanged, sendPasswordResetEmail, signOut, signInWithEmailAndPassword, onIdTokenChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { CALLABLES, SUBSCRIPTIONS, isApiErrorDetails } from '@levelup/api-contract';
import { ref, onValue } from 'firebase/database';

// src/config/region.ts
var DEFAULT_REGION = "asia-south1";
function resolveRegion(opts) {
  return opts?.region ?? DEFAULT_REGION;
}
var EMPTY_PATH_CONTEXT = { tenantId: "", uid: "" };
var TENANT_PLACEHOLDER = "__tenant__";
var UID_PLACEHOLDER = "__uid__";
var TENANT_CLAIM = "tenantId";
async function readContext(user) {
  if (!user) return EMPTY_PATH_CONTEXT;
  const token = await user.getIdTokenResult();
  const claimTenant = token.claims[TENANT_CLAIM];
  return {
    uid: user.uid,
    tenantId: typeof claimTenant === "string" ? claimTenant : ""
  };
}
function createPathContextHolder(auth) {
  let current = EMPTY_PATH_CONTEXT;
  if (auth.currentUser) {
    current = { tenantId: "", uid: auth.currentUser.uid };
  }
  const stop = onIdTokenChanged(auth, (user) => {
    void readContext(user).then((ctx) => {
      current = ctx;
    });
  });
  return {
    get: () => current,
    async refresh(force = true) {
      const user = auth.currentUser;
      if (user && force) {
        await user.getIdToken(true);
      }
      current = await readContext(user);
    },
    dispose: stop
  };
}
var AI_CLIENT_TIMEOUT_MS = 54e4;
function toDeployedCallableId(name) {
  return name.replace(/\./g, "-");
}
async function invokeViaCallable(functions, name, data) {
  const isAiTier = CALLABLES[name]?.rateTier === "ai";
  const callable = httpsCallable(
    functions,
    toDeployedCallableId(name),
    isAiTier ? { timeout: AI_CLIENT_TIMEOUT_MS } : void 0
  );
  const result = await callable(data);
  return result.data;
}

// src/subscribe/subscription-sources.ts
var T = TENANT_PLACEHOLDER;
var U = UID_PLACEHOLDER;
function leaderboardNode(scope, spaceId, storyPointId) {
  switch (scope) {
    case "tenant":
      return `leaderboards/${T}/tenant`;
    case "class":
      return `leaderboards/${T}/class`;
    case "space":
      return `leaderboards/${T}/space/${spaceId ?? "_"}`;
    case "storyPoint":
      return `leaderboards/${T}/storyPoint/${spaceId ?? "_"}/${storyPointId ?? "_"}`;
  }
}
var SUBSCRIPTION_SOURCES = {
  // U2.6: levelup live tickers (AD-12) — slim RTDB projections written by the
  // levelup services (packages/services levelup/levelup-projection.ts), NOT
  // Firestore. All roots are USER-owned ({userId} == auth uid, AD-9); the RTDB
  // read rules gate owner access on the path segment.
  // CHAT-1 (AD-12 addendum): the BUMP node — {rev, lastMessageAt} only; message
  // content NEVER rides RTDB. Each bump debounce-refetches getChatSession
  // (signal-over-RTDB, data-over-callable). Was the last firestore-backed sub.
  "v1.levelup.chatStream": {
    backend: "rtdb",
    resolve: ({ sessionId }) => ({
      kind: "rtdb",
      nodePath: `chatBump/${T}/${U}/${sessionId}`
    })
  },
  "v1.levelup.testSessionDeadline": {
    backend: "rtdb",
    resolve: ({ sessionId }) => ({
      kind: "rtdb",
      nodePath: `testSessionLive/${T}/${U}/${sessionId}`
    })
  },
  "v1.levelup.spaceProgressLive": {
    backend: "rtdb",
    // `userId` is a PARAM (a teacher/parent may watch another learner's node —
    // the RTDB rules grant roles + owner), unlike the self-only `__uid__` channels.
    resolve: ({ spaceId, userId }) => ({
      kind: "rtdb",
      nodePath: `spaceProgressLive/${T}/${userId}/${spaceId}`
    })
  },
  "v1.levelup.studentLevelLive": {
    backend: "rtdb",
    resolve: () => ({ kind: "rtdb", nodePath: `studentLevelLive/${T}/${U}` })
  },
  "v1.levelup.achievementUnlock": {
    backend: "rtdb",
    // The LATEST unlock event (last-write-wins node; cleared on mark-seen).
    resolve: () => ({ kind: "rtdb", nodePath: `achievementUnlocks/${T}/${U}/latest` })
  },
  "v1.levelup.leaderboardLive": {
    backend: "rtdb",
    resolve: ({ scope, spaceId, storyPointId }) => ({
      kind: "rtdb",
      nodePath: leaderboardNode(scope, spaceId, storyPointId)
    })
  },
  "v1.notification.badge": {
    backend: "rtdb",
    resolve: () => ({ kind: "rtdb", nodePath: `notifications/${T}/${U}` })
  },
  // AG-5: autograde live grading ticker (AD-12) — slim RTDB projections written by
  // the pipeline (packages/services autograde/pipeline/grading-projection.ts), NOT
  // Firestore. The client reads the payload LEAF (`/status`, `/agg`); the sibling
  // gate/index nodes stay server-only via the RTDB read rules.
  "v1.autograde.gradingStatus": {
    backend: "rtdb",
    resolve: ({ submissionId }) => ({
      kind: "rtdb",
      nodePath: `gradingProgress/${T}/submission/${submissionId}/status`
    })
  },
  "v1.autograde.examGrading": {
    backend: "rtdb",
    resolve: ({ examId }) => ({
      kind: "rtdb",
      nodePath: `gradingProgress/${T}/exam/${examId}/agg`
    })
  },
  // Live question-extraction + rubric-generation progress. Slim status leaf;
  // parent stays deny-all. Written by extractQuestions across its two passes.
  "v1.autograde.extractionStatus": {
    backend: "rtdb",
    resolve: ({ examId }) => ({
      kind: "rtdb",
      nodePath: `extractionProgress/${T}/exam/${examId}/status`
    })
  }
};
function applyPathContext(path, ctx) {
  return path.split(TENANT_PLACEHOLDER).join(ctx.tenantId).split(UID_PLACEHOLDER).join(ctx.uid);
}
var PayloadValidationError = class extends Error {
  constructor(subscription, issues) {
    super(`[transport-firebase] payload for "${subscription}" failed schema validation`);
    this.subscription = subscription;
    this.issues = issues;
    this.name = "PayloadValidationError";
  }
};
function validatePayload(name, raw, mode) {
  const schema = SUBSCRIPTIONS[name].payload;
  if (mode === "dev") {
    return schema.parse(raw);
  }
  const result = schema.safeParse(raw);
  if (!result.success) {
    throw new PayloadValidationError(name, result.error.issues);
  }
  return result.data;
}

// src/subscribe/to-transport-error.ts
function coerceCode(raw) {
  if (typeof raw !== "string") return "INTERNAL_ERROR";
  switch (raw) {
    case "permission-denied":
      return "PERMISSION_DENIED";
    case "unauthenticated":
      return "UNAUTHENTICATED";
    case "not-found":
      return "NOT_FOUND";
    case "resource-exhausted":
      return "RATE_LIMITED";
    case "failed-precondition":
      return "PRECONDITION_FAILED";
    default:
      return "INTERNAL_ERROR";
  }
}
function toTransportError(err) {
  const message = err && typeof err === "object" && "message" in err ? String(err.message) : "realtime stream error";
  const code = coerceCode(
    err && typeof err === "object" && "code" in err ? err.code : void 0
  );
  return { code, message, retryable: false };
}

// src/subscribe/subscribe-via-rtdb.ts
var rtdbSubSeq = 0;
function subscribeViaRTDB(rtdb, descriptor, params, ctx, cb, mode, name) {
  const target = descriptor.resolve(params);
  const nodeRef = ref(rtdb, applyPathContext(target.nodePath, ctx));
  const id = `rtdb_${name}_${rtdbSubSeq++}`;
  let active = true;
  let synced = false;
  const detach = onValue(
    nodeRef,
    (snap) => {
      if (!synced) {
        synced = true;
        cb.onSynced?.();
      }
      if (snap.val() === null) return;
      try {
        cb.next(validatePayload(name, snap.val(), mode));
      } catch (err) {
        cb.error?.(toTransportError(err));
      }
    },
    (err) => cb.error?.(toTransportError(err))
  );
  return {
    id,
    get active() {
      return active;
    },
    unsubscribe() {
      if (!active) return;
      active = false;
      detach();
    }
  };
}

// src/subscribe/subscribe.ts
function normalize(cb) {
  return typeof cb === "function" ? { next: cb } : cb;
}
function createSubscribe(services, getCtx, mode) {
  return function subscribe(name, params, cb) {
    const callbacks = normalize(cb);
    const descriptor = SUBSCRIPTION_SOURCES[name];
    return subscribeViaRTDB(
      services.rtdb,
      descriptor,
      params,
      getCtx(),
      callbacks,
      mode,
      name
    );
  };
}
var offsetSubSeq = 0;
function createServerTimeOffset(rtdb) {
  return function serverTimeOffset(cb) {
    const offsetRef = ref(rtdb, "/.info/serverTimeOffset");
    const id = `serverTimeOffset_${offsetSubSeq++}`;
    let active = true;
    const detach = onValue(offsetRef, (snap) => {
      const val = snap.val();
      cb(typeof val === "number" ? val : 0);
    });
    return {
      id,
      get active() {
        return active;
      },
      unsubscribe() {
        if (!active) return;
        active = false;
        detach();
      }
    };
  };
}

// src/auth/refresh-token.ts
function createRefreshToken(auth, onRefreshed) {
  return async function refreshToken(force = true) {
    await auth.currentUser?.getIdToken(force);
    await onRefreshed(force);
  };
}

// src/storage/storage-transport.ts
function toBodyInit(bytes) {
  if (bytes instanceof Uint8Array) return bytes;
  return bytes;
}
function createStorageTransport(deps) {
  return {
    requestUploadUrl(data) {
      return invokeViaCallable(deps.functions, "v1.autograde.requestUploadUrl", data);
    },
    async uploadImage(input) {
      const res = await fetch(input.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": input.contentType },
        body: toBodyInit(input.bytes)
      });
      if (!res.ok) {
        throw new Error(
          `[transport-firebase] signed-PUT upload failed: ${res.status} ${res.statusText}`
        );
      }
    }
  };
}

// src/create-firebase-transport.ts
function createFirebaseTransport(services, opts = {}) {
  const mode = opts.validatePayloads ? "dev" : "prod";
  const ctxHolder = createPathContextHolder(services.auth);
  const subscribe = createSubscribe(services, () => ctxHolder.get(), mode);
  const serverTimeOffset = createServerTimeOffset(services.rtdb);
  const refreshToken = createRefreshToken(services.auth, (force) => ctxHolder.refresh(force));
  const storage = createStorageTransport({
    functions: services.functions,
    storage: services.storage
  });
  return {
    invoke(name, data) {
      return invokeViaCallable(services.functions, name, data);
    },
    subscribe,
    serverTimeOffset,
    refreshToken,
    storage
  };
}
function toSnapshot(user) {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName
  };
}
function createFirebaseAuthHandle(auth) {
  return {
    async signIn(email, password) {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      return { user: toSnapshot(cred.user) };
    },
    async signOut() {
      await signOut(auth);
    },
    async sendPasswordReset(email) {
      await sendPasswordResetEmail(auth, email);
    },
    onAuthState(cb) {
      return onAuthStateChanged(auth, (user) => cb(toSnapshot(user)));
    },
    currentUser() {
      return toSnapshot(auth.currentUser);
    }
  };
}
function unwrapCallableError(err) {
  if (err && typeof err === "object" && "details" in err) {
    const details = err.details;
    if (isApiErrorDetails(details)) return details;
  }
  return err;
}

export { DEFAULT_REGION, PayloadValidationError, SUBSCRIPTION_SOURCES, createFirebaseAuthHandle, createFirebaseTransport, invokeViaCallable, resolveRegion, subscribeViaRTDB, toDeployedCallableId, toTransportError, unwrapCallableError, validatePayload };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map